import type { CccdParsedData } from './cccdOcr';
import { normalizeDateForInput } from './cccdOcr';

/** Chuẩn hóa SĐT Việt Nam từ chuỗi QR (0xxxxxxxxx, 9xxxxxxxxx, +84...) */
export function normalizeVnPhone(value?: string | null): string | undefined {
  if (!value?.trim()) return undefined;

  let digits = value.replace(/\D/g, '');
  if (!digits) return undefined;

  if (digits.startsWith('84')) {
    const national = digits.slice(2);
    if (/^(3|5|7|8|9)\d{8}$/.test(national)) {
      return `0${national}`;
    }
  }

  if (/^(3|5|7|8|9)\d{8}$/.test(digits)) {
    return `0${digits}`;
  }

  if (/^0(3|5|7|8|9)\d{8}$/.test(digits)) {
    return digits;
  }

  return undefined;
}

/** Chuẩn hóa ngày sinh từ QR CCCD (DDMMYYYY, DD/MM/YYYY, YYYY-MM-DD, ...) */
function normalizeQrDate(value?: string): string | undefined {
  if (!value?.trim()) return undefined;
  const trimmed = value.trim();

  const iso = normalizeDateForInput(trimmed);
  if (iso) return iso;

  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 8) {
    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 8);
    return `${year}-${month}-${day}`;
  }

  return trimmed;
}

function splitQrFields(rawValue: string): string[] {
  const normalized = rawValue.replace(/^\uFEFF/, '').trim();
  const byPipe = normalized.split(/[|｜]/).map((field) => field.trim());
  if (byPipe.length >= 3) return byPipe;

  const byNewline = normalized
    .split(/\r?\n/)
    .map((field) => field.trim())
    .filter(Boolean);
  if (byNewline.length >= 3) return byNewline;

  return byPipe;
}

function extractPhoneFromLabeledValue(value?: string): string | undefined {
  if (!value?.trim()) return undefined;

  const labeled = value.match(/(?:sđt|sdt|phone|mobile|tel|điện\s*thoại)\s*[:\-]?\s*([+\d\s().-]+)/i);
  if (labeled) {
    const phone = normalizeVnPhone(labeled[1]);
    if (phone) return phone;
  }

  return normalizeVnPhone(value);
}

function extractPhoneFromFields(fields: string[], skipIndices: Set<number> = new Set()): string | undefined {
  for (let index = 0; index < fields.length; index += 1) {
    if (skipIndices.has(index)) continue;
    const phone = extractPhoneFromLabeledValue(fields[index]);
    if (phone) return phone;
  }
  return undefined;
}

function extractPhoneFromText(text?: string): string | undefined {
  if (!text?.trim()) return undefined;
  const matches = text.match(/(?:\+?84|0)(?:[\s.-]?\d){8,10}/g);
  if (!matches) return undefined;

  for (const match of matches) {
    const phone = normalizeVnPhone(match);
    if (phone) return phone;
  }

  return undefined;
}

/** CCCD gắn chip: 12 chữ số */
function isCccdNumber(value?: string): boolean {
  const digits = value?.replace(/\D/g, '') ?? '';
  return digits.length === 12;
}

function looksLikeGender(value?: string): boolean {
  if (!value?.trim()) return false;
  return /^(nam|nữ|male|female)$/i.test(value.trim());
}

function resolvePhoneFromPayload(fields: string[], preferredIndex = 1): string | undefined {
  const preferred = extractPhoneFromLabeledValue(fields[preferredIndex]);
  if (preferred) return preferred;

  return (
    extractPhoneFromFields(fields, new Set([0]))
    || extractPhoneFromText(fields.join('|'))
  );
}

/**
 * QR mặt trước CCCD gắn chip (C06):
 * CCCD(12 số) | CMND cũ | Họ tên | Ngày sinh | Giới tính | Địa chỉ | Ngày cấp
 *
 * Biến thể VNeID / app quét:
 * CCCD(12 số) | SĐT | Tên | Ngày sinh | Giới tính | Địa chỉ
 *
 * Biến thể 5 trường: CCCD | SĐT | Tên | Ngày sinh | Địa chỉ
 */
function parseCccdPipeFormat(fields: string[]): CccdParsedData | null {
  if (fields.length < 5 || !isCccdNumber(fields[0])) return null;

  const hasGender = looksLikeGender(fields[4]);
  const addressIndex = hasGender ? 5 : 4;

  if (addressIndex >= fields.length) return null;

  return {
    cccd: fields[0].replace(/\D/g, ''),
    phone: resolvePhoneFromPayload(fields, 1),
    name: fields[2]?.trim() || undefined,
    dateOfBirth: normalizeQrDate(fields[3]),
    address: fields[addressIndex]?.trim() || undefined,
    engine: 'cccd-qr',
  };
}

function parsePipeFields(fields: string[]): CccdParsedData {
  const parsed = parseCccdPipeFormat(fields);
  if (parsed) return parsed;

  // Fallback tối thiểu khi QR không đúng chuẩn 5/7 trường
  if (fields.length >= 3 && isCccdNumber(fields[0])) {
    return {
      cccd: fields[0].replace(/\D/g, ''),
      phone: resolvePhoneFromPayload(fields, 1),
      name: fields[2]?.trim() || undefined,
      dateOfBirth: normalizeQrDate(fields[3]),
      address: fields[4]?.trim() || undefined,
      engine: 'cccd-qr',
    };
  }

  return {
    cccd: isCccdNumber(fields[0]) ? fields[0].replace(/\D/g, '') : undefined,
    phone: extractPhoneFromText(fields.join('|')),
    name: fields.find((field) => looksLikeGender(field) ? false : /[A-Za-zÀ-ỹ]{2,}/.test(field)),
    engine: 'cccd-qr',
  };
}

/**
 * Parse payload QR trên CCCD gắn chip Việt Nam.
 */
export function parseCccdQrPayload(rawValue: string): CccdParsedData {
  const value = rawValue.trim();
  if (!value) return {};

  try {
    const json = JSON.parse(value) as Record<string, unknown>;
    const address = String(json.address ?? json.residence ?? json.place_of_residence ?? '').trim() || undefined;

    const parsed: CccdParsedData = {
      name: String(json.name ?? json.full_name ?? json.fullName ?? '').trim() || undefined,
      cccd: String(json.cccd ?? json.id_number ?? json.idNumber ?? json.citizen_id ?? '').replace(/\D/g, '') || undefined,
      dateOfBirth: normalizeQrDate(String(json.dateOfBirth ?? json.date_of_birth ?? json.dob ?? '')),
      address,
      phone:
        extractPhoneFromLabeledValue(String(json.phone ?? json.mobile ?? json.phoneNumber ?? json.phone_number ?? json.tel ?? json.so_dien_thoai ?? json.sdt ?? json.soDienThoai ?? json.dienThoai ?? ''))
        || extractPhoneFromText(value),
      engine: 'cccd-qr',
    };

    if (!parsed.phone) {
      parsed.phone = extractPhoneFromText(value);
    }

    return parsed;
  } catch {
    const fields = splitQrFields(value);
    const parsed = parsePipeFields(fields);
    if (!parsed.phone) {
      parsed.phone = extractPhoneFromText(value);
    }
    return parsed;
  }
}
